import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../../config/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const CreateCuminIssueModal = ({ isOpen, onClose, task, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cuminCredentials, setCuminCredentials] = useState(null);
  const [loadingMetadata, setLoadingMetadata] = useState(false);

  // Form states
  const [projectId, setProjectId] = useState('');
  const [issueTitle, setIssueTitle] = useState('');
  const [issueDescription, setIssueDescription] = useState('');
  const [issueType, setIssueType] = useState('');
  const [issuePriority, setIssuePriority] = useState('');
  const [issueStatus, setIssueStatus] = useState('To Do');
  const [parentIssueId, setParentIssueId] = useState('');

  // CUMIN metadata
  const [projects, setProjects] = useState([]);
  const [issueTypes, setIssueTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [parentIssues, setParentIssues] = useState([]);
  const [loadingParentIssues, setLoadingParentIssues] = useState(false);

  // Rich Text Editor ref
  const quillRef = useRef(null);

  // Rich Text Editor Configuration
  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'link'
  ];

  useEffect(() => {
    if (isOpen && task) {
      // Pre-fill form with task data
      setIssueTitle(task.title || '');

      // Keep HTML description as-is for rich text editor
      const description = task.description || '';
      setIssueDescription(description);

      // Set content in Quill editor after a short delay to ensure it's mounted
      setTimeout(() => {
        const quillEditor = quillRef.current?.getEditor();
        if (quillEditor && description) {
          quillEditor.root.innerHTML = description;
        }
      }, 100);

      // Load CUMIN credentials and metadata
      loadCuminData();
    } else if (isOpen && !task) {
      // Reset form when opening without task
      setIssueTitle('');
      setIssueDescription('');
      setTimeout(() => {
        const quillEditor = quillRef.current?.getEditor();
        if (quillEditor) {
          quillEditor.setText('');
        }
      }, 100);
    }
  }, [isOpen, task]);

  const loadCuminData = async () => {
    const token = localStorage.getItem('token');

    try {
      // Check CUMIN credentials via WorkLoop backend
      const credentialsRes = await axios.get(`${API_URL}/cumin/credentials`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setCuminCredentials(credentialsRes.data);

      if (credentialsRes.data.isConnected) {
        // Load metadata
        setLoadingMetadata(true);
        const [projectsRes, typesRes, prioritiesRes, statusesRes] = await Promise.all([
          axios.get(`${API_URL}/cumin/projects?page=0&size=100`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/cumin/issue-types`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/cumin/priorities`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/cumin/statuses`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => ({ data: [] })),
        ]);

        setProjects(projectsRes.data || []);
        // Filter out Story and Epic from issue types
        const filteredIssueTypes = (typesRes.data || []).filter(t => {
          const value = t.value || t.label || t.name || t.type || '';
          return value !== 'Story' && value !== 'Epic';
        });
        setIssueTypes(filteredIssueTypes);
        setPriorities(prioritiesRes.data || []);
        setStatuses(statusesRes.data || []);

        // Set defaults - prefer Bug or Task (Story and Epic are not available)
        if (filteredIssueTypes && filteredIssueTypes.length > 0) {
          // Find Bug first, then Task, fallback to first item
          const preferredType = filteredIssueTypes.find(t => {
            const value = t.value || t.label || t.name || t.type || '';
            return value === 'Bug' || value === 'Task';
          });
          const defaultType = preferredType || filteredIssueTypes[0];
          setIssueType(defaultType.value || defaultType.label || defaultType.name || defaultType.type || '');
        }
        if (prioritiesRes.data && prioritiesRes.data.length > 0) {
          setIssuePriority(prioritiesRes.data[0].value || prioritiesRes.data[0].label || prioritiesRes.data[0].name || prioritiesRes.data[0].priority || '');
        }
      }
    } catch (error) {
      if (error.response?.status === 400 || error.response?.status === 404) {
        setError('CUMIN credentials not found. Please connect to CUMIN first.');
      } else {
        setError('Failed to load CUMIN data');
      }
    } finally {
      setLoadingMetadata(false);
    }
  };

  // Load parent issues when project or issue type changes
  useEffect(() => {
    const loadParentIssues = async () => {
      if (!projectId || !cuminCredentials?.isConnected) {
        setParentIssues([]);
        return;
      }

      // Only load parent issues if issueType requires a parent (Bug, Improvement, Task, Subtask)
      if (!issueType || (issueType !== 'Bug' && issueType !== 'Improvement' && issueType !== 'Task' && issueType !== 'Subtask')) {
        setParentIssues([]);
        return;
      }

      setLoadingParentIssues(true);
      const token = localStorage.getItem('token');

      try {
        // Always pass issueType if available, so CUMIN can filter valid parents
        const url = `${API_URL}/cumin/parent-issues?projectId=${projectId}${issueType ? `&issueType=${encodeURIComponent(issueType)}` : ''}`;
        console.log('Loading parent issues with URL:', url);
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log('Parent issues response:', response.data);
        setParentIssues(response.data || []);
      } catch (error) {
        console.error('Failed to load parent issues:', error);
        console.error('Error details:', error.response?.data);
        setParentIssues([]);
      } finally {
        setLoadingParentIssues(false);
      }
    };

    // Add a small delay to ensure projectId and issueType are both set
    const timer = setTimeout(() => {
      loadParentIssues();
    }, 100);

    return () => clearTimeout(timer);
  }, [projectId, issueType, cuminCredentials]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');

      // Get HTML content from Quill editor if available
      let descriptionContent = issueDescription;
      const quillEditor = quillRef.current?.getEditor();
      if (quillEditor) {
        descriptionContent = quillEditor.root.innerHTML;
      }

      // Validate that description has meaningful content (not just empty HTML)
      const tmp = document.createElement('div');
      tmp.innerHTML = descriptionContent;
      const textContent = tmp.textContent || tmp.innerText || '';
      if (!textContent.trim()) {
        setError('Description is required');
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${API_URL}/cumin/issues`,
        {
          title: issueTitle,
          description: descriptionContent,
          projectId: parseInt(projectId),
          issueType,
          priority: issuePriority,
          status: issueStatus,
          parentIssueId: parentIssueId ? parseInt(parentIssueId) : null,
          taskId: task?.id || null, // Include task ID to map CUMIN issue to task
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Open issue in new tab
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      }

      // Call success callback (parent component will refresh tasks)
      if (onSuccess) {
        onSuccess(response.data, task?.id);
      }

      // Close modal
      onClose();
    } catch (error) {
      let errorMessage = 'Failed to create CUMIN issue';
      if (error.response?.data) {
        // Handle string error messages
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      }

      // Provide helpful message for Task/Subtask/Improvement/Bug parent requirement
      if (errorMessage.includes('parent') || errorMessage.includes('cannot be child')) {
        errorMessage = `${errorMessage}. Note: "Task", "Subtask", "Improvement", and "Bug" issue types require a parent issue. Please select a parent issue.`;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Raise CUMIN Issue
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
            </div>
          )}

          {!cuminCredentials ? (
            <div className="text-center py-8">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                CUMIN credentials not found. Please connect to CUMIN first.
              </p>
              <button
                onClick={() => {
                  onClose();
                  window.location.href = '/integrations?plugin=cumin';
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Go to CUMIN Settings
              </button>
            </div>
          ) : !cuminCredentials.isConnected ? (
            <div className="text-center py-8">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                CUMIN connection is not active. Please test your connection first.
              </p>
              <button
                onClick={() => {
                  onClose();
                  window.location.href = '/integrations?plugin=cumin';
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Go to CUMIN Settings
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {loadingMetadata && (
                <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                  Loading CUMIN metadata...
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Project *
                </label>
                <select
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select a project</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name} {project.projectKey ? `(${project.projectKey})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Issue Title *
                </label>
                <input
                  type="text"
                  value={issueTitle}
                  onChange={(e) => setIssueTitle(e.target.value)}
                  placeholder="Enter issue title"
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description *
                </label>
                <div className="relative">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={issueDescription}
                    onChange={(content) => setIssueDescription(content)}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Enter issue description"
                    className="bg-white dark:bg-gray-700 rounded-lg"
                    style={{
                      minHeight: "150px",
                      maxHeight: "300px",
                    }}
                  />
                  {/* Custom Quill Styles */}
                  <style>{`
                    .ql-container {
                      font-size: 14px;
                      min-height: 150px;
                      max-height: 300px;
                      overflow-y: auto;
                      overflow-x: hidden;
                    }
                    .ql-editor {
                      min-height: 150px;
                      color: #111827;
                      overflow-x: hidden;
                      word-wrap: break-word;
                      word-break: break-word;
                    }
                    .dark .ql-editor {
                      color: #f9fafb;
                    }
                    .ql-toolbar {
                      border-top-left-radius: 0.5rem;
                      border-top-right-radius: 0.5rem;
                      border-bottom: 1px solid #e5e7eb;
                      overflow-x: hidden !important;
                      overflow-y: hidden !important;
                      white-space: normal !important;
                      display: flex !important;
                      flex-wrap: wrap !important;
                    }
                    .ql-toolbar .ql-formats {
                      display: inline-flex !important;
                      flex-wrap: wrap !important;
                    }
                    .dark .ql-toolbar {
                      background: #374151;
                      border-bottom-color: #4b5563;
                    }
                    .dark .ql-stroke {
                      stroke: #d1d5db;
                    }
                    .dark .ql-fill {
                      fill: #d1d5db;
                    }
                    .dark .ql-picker-label {
                      color: #d1d5db;
                    }
                    .ql-container {
                      border-bottom-left-radius: 0.5rem;
                      border-bottom-right-radius: 0.5rem;
                      border: 1px solid #d1d5db;
                    }
                    .dark .ql-container {
                      border-color: #4b5563;
                    }
                  `}</style>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Issue Type
                  </label>
                  <select
                    value={issueType}
                    onChange={(e) => setIssueType(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select type</option>
                    {issueTypes.map((type, idx) => {
                      const typeValue = type.value || type.label || type.name || type.type || type;
                      return (
                        <option key={idx} value={typeValue}>
                          {type.label || type.value || type.name || type.type || type}
                        </option>
                      );
                    })}
                  </select>
                  <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                    ℹ️ Note: Epic and Story issue types are not available here. Please create them directly in CUMIN.
                  </p>
                  {(issueType === 'Task' || issueType === 'Subtask' || issueType === 'Improvement' || issueType === 'Bug') && (
                    <p className="mt-1 text-xs text-yellow-600 dark:text-yellow-400">
                      ⚠️ {issueType} type requires a parent issue. Please select a parent issue below.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Priority
                  </label>
                  <select
                    value={issuePriority}
                    onChange={(e) => setIssuePriority(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select priority</option>
                    {priorities.map((priority, idx) => (
                      <option key={idx} value={priority.value || priority.label || priority.name || priority.priority || priority}>
                        {priority.label || priority.value || priority.name || priority.priority || priority}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <select
                    value={issueStatus}
                    onChange={(e) => setIssueStatus(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    {statuses.length > 0 ? (
                      statuses.map((status, idx) => (
                        <option key={idx} value={status.value || status.label || status.name || status.status || status}>
                          {status.label || status.value || status.name || status.status || status}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="To Do">To Do</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Done">Done</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              {/* Parent Issue Dropdown */}
              {/* Show for Bug (Story/Task/SubTask), Improvement (Story/Task/SubTask), Task (Story), Subtask (Task) */}
              {projectId && (issueType === 'Bug' || issueType === 'Improvement' || issueType === 'Task' || issueType === 'Subtask') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Parent Issue {((issueType === 'Task' || issueType === 'Subtask' || issueType === 'Improvement' || issueType === 'Bug') && !parentIssueId) && <span className="text-red-500">*</span>}
                  </label>
                  <select
                    value={parentIssueId}
                    onChange={(e) => setParentIssueId(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    disabled={loadingParentIssues}
                  >
                    <option value="">{loadingParentIssues ? 'Loading parent issues...' : 'Select parent issue (optional)'}</option>
                    {parentIssues.map((issue, idx) => {
                      // Handle multiple possible field names for compatibility
                      const issueId = issue.id || issue.issueId || issue.parentIssueId;
                      const issueKey = issue.key || issue.issueKey || issue.parentIssueKey || issue.issue_key || '';
                      const issueTitle = issue.title || issue.name || issue.parentIssueTitle || '';
                      const issueType = issue.issueType || issue.issue_type || issue.type || '';
                      // Display format: HEAL-1: Web Development [Task] or Epic Name [Epic] (#123)
                      const displayText = issueKey
                        ? `${issueKey}: ${issueTitle}${issueType ? ` [${issueType}]` : ''}`
                        : `${issueTitle}${issueType ? ` [${issueType}]` : ''}${issueId ? ` (#${issueId})` : ''}`;
                      return (
                        <option key={issueId || idx} value={issueId}>
                          {displayText}
                        </option>
                      );
                    })}
                  </select>
                  {parentIssues.length === 0 && !loadingParentIssues && projectId && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      No parent issues available for this project. You can create top-level issues.
                    </p>
                  )}
                  {(issueType === 'Bug' || issueType === 'Improvement') && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {issueType} requires a parent issue (Story, Task, or SubTask).
                    </p>
                  )}
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating...' : 'Create Issue'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateCuminIssueModal;
